import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { BarChart3, Package, TrendingUp, History } from 'lucide-react';

const BudgetHistoryStats = () => {
  const { user } = useAuth();
  const [globalStats, setGlobalStats] = useState(null);
  const [productsHistory, setProductsHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Carregar estatísticas globais
      const globalRes = await fetch('/api/budget-sheet-items/history/global', { headers });
      const globalData = await globalRes.json();
      if (globalData.success) setGlobalStats(globalData.data);

      // Carregar histórico de produtos
      const productsRes = await fetch('/api/budget-sheet-items/history/all', { headers });
      const productsData = await productsRes.json();
      if (productsData.success) setProductsHistory(productsData.data);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  if (loading) {
    return <div className="p-6 text-center">Carregando...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <History className="w-8 h-8 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-bold">Histórico de Orçamento</h1>
          <p className="text-gray-600">Estatísticas baseadas em orçamentos anteriores</p>
        </div>
      </div>

      {/* Estatísticas Globais */}
      {globalStats && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Média Global do Portfólio
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-sm opacity-80">Média Margem</div>
              <div className="text-2xl font-bold">{globalStats.global_avg_margin}%</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-sm opacity-80">Média Peso</div>
              <div className="text-2xl font-bold">{globalStats.global_avg_weight}%</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-sm opacity-80">Produtos Usados</div>
              <div className="text-2xl font-bold">{globalStats.produtos_usados}</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-sm opacity-80">Total Itens</div>
              <div className="text-2xl font-bold">{globalStats.total_itens_orcamentados}</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-sm opacity-80">Orçamentos</div>
              <div className="text-2xl font-bold">{globalStats.total_orcamentos}</div>
            </div>
          </div>
        </div>
      )}

      {/* Histórico por Produto */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Package className="w-5 h-5" />
            Histórico por Produto
          </h2>
        </div>

        {productsHistory.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhum histórico disponível ainda.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Produto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Categoria</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Usos</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 bg-blue-50">Margem Média</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 bg-blue-50">Margem Mín</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 bg-blue-50">Margem Máx</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 bg-purple-50">Peso Médio</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 bg-purple-50">Peso Mín</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 bg-purple-50">Peso Máx</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {productsHistory.map((product, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{product.produto_nome}</div>
                      <div className="text-xs text-gray-500">{product.produto_sku}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {product.categoria || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-gray-100 rounded text-sm font-medium">
                        {product.total_usos}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center bg-blue-50">
                      <span className="font-bold text-blue-700">
                        {parseFloat(product.avg_margin_percentage).toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center bg-blue-50 text-gray-600">
                      {parseFloat(product.min_margin_percentage).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-center bg-blue-50 text-gray-600">
                      {parseFloat(product.max_margin_percentage).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-center bg-purple-50">
                      <span className="font-bold text-purple-700">
                        {parseFloat(product.avg_weight_percentage).toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center bg-purple-50 text-gray-600">
                      {parseFloat(product.min_weight_percentage).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-center bg-purple-50 text-gray-600">
                      {parseFloat(product.max_weight_percentage).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legenda */}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
        <p className="font-medium mb-2">Entenda as estatísticas:</p>
        <ul className="space-y-1">
          <li>• <strong>Margem %:</strong> Porcentagem de lucro aplicada sobre o custo do produto</li>
          <li>• <strong>Peso %:</strong> Taxa adicional por dificuldade/exclusividade do serviço</li>
          <li>• <strong>Estatísticas:</strong> Calculadas apenas com orçamentos confirmados (status CONFIRMED)</li>
        </ul>
      </div>
    </div>
  );
};

export default BudgetHistoryStats;
